import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DemoModalService } from '../../../core/demo-modal.service';

const RECIPIENT = 'soporte@gesvatec.com';

@Component({
  selector: 'app-demo-modal',
  templateUrl: './demo-modal.component.html',
  styleUrls: ['./demo-modal.component.scss'],
})
export class DemoModalComponent implements OnInit, OnDestroy {
  open = false;
  sent = false;

  // Listas de opciones (desde i18n; se actualizan al cambiar de idioma).
  roles: string[] = [];
  productos: string[] = [];

  // Estado del formulario.
  empresa = '';
  rol = '';
  rolOtros = false;
  rolOtrosTexto = '';
  productosSel = new Set<string>();
  otrosChecked = false;
  otrosTexto = '';
  contacto = '';
  email = '';
  showProductosError = false;

  private readonly subs = new Subscription();

  constructor(
    private readonly modal: DemoModalService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.modal.open$.subscribe((isOpen) => {
        this.open = isOpen;
        document.body.classList.toggle('modal-active', isOpen);
        if (isOpen) {
          this.reset();
        }
      }),
    );
    // stream() emite al cargar las traducciones y en cada cambio de idioma.
    this.subs.add(this.translate.stream('demo.roles').subscribe((v) => (this.roles = v)));
    this.subs.add(this.translate.stream('demo.productos').subscribe((v) => (this.productos = v)));
  }

  close(): void {
    this.modal.close();
  }

  onOverlayClick(e: MouseEvent): void {
    if (e.target === e.currentTarget) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.open) {
      this.close();
    }
  }

  onRolChange(value: string): void {
    this.rol = value;
    // "Otros" es siempre la última opción de la lista.
    this.rolOtros = value === this.roles[this.roles.length - 1];
    if (!this.rolOtros) {
      this.rolOtrosTexto = '';
    }
  }

  onProducto(value: string, checked: boolean): void {
    if (checked) {
      this.productosSel.add(value);
    } else {
      this.productosSel.delete(value);
    }
    this.clearProductosError();
  }

  onOtros(checked: boolean): void {
    this.otrosChecked = checked;
    if (!checked) {
      this.otrosTexto = '';
    }
    this.clearProductosError();
  }

  submit(form: NgForm): void {
    const productosOk = this.productosSel.size > 0 || this.otrosChecked;
    this.showProductosError = !productosOk;
    if (form.invalid || !productosOk) {
      Object.values(form.controls).forEach((c) => c.markAsTouched());
      return;
    }
    // Sin backend: se abre el cliente de correo con un email pre-rellenado.
    this.sendMail();
    this.sent = true;
  }

  /** Abre el cliente de correo del visitante con la solicitud pre-rellenada. */
  private sendMail(): void {
    const rolVal = this.rolOtros ? this.rolOtrosTexto || 'Otros' : this.rol;
    const productos = [...this.productosSel];
    if (this.otrosChecked) {
      productos.push('Otros' + (this.otrosTexto ? `: ${this.otrosTexto}` : ''));
    }
    const body = [
      'Nueva solicitud de demostración — GESVATEC PackCert',
      '',
      `Empresa: ${this.empresa}`,
      `Rol de la empresa: ${rolVal}`,
      `Productos que gestiona: ${productos.join(', ')}`,
      `Persona de contacto: ${this.contacto}`,
      `Email de contacto: ${this.email}`,
    ].join('\r\n');
    const subject = `Solicitud de demo — ${this.empresa}`;
    const url = `mailto:${RECIPIENT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  }

  private clearProductosError(): void {
    if (this.productosSel.size > 0 || this.otrosChecked) {
      this.showProductosError = false;
    }
  }

  private reset(): void {
    this.sent = false;
    this.empresa = '';
    this.rol = '';
    this.rolOtros = false;
    this.rolOtrosTexto = '';
    this.productosSel.clear();
    this.otrosChecked = false;
    this.otrosTexto = '';
    this.contacto = '';
    this.email = '';
    this.showProductosError = false;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    document.body.classList.remove('modal-active');
  }
}
